import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
    Mail,
    MapPin,
    Globe,
    Send,
    Loader2,
    ArrowUpRight,
    CheckCircle2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
    "AI Strategy & Planning",
    "Creative & Digital Ads",
    "Websites & Apps",
    "Interactive Activations",
    "Analytics & Reporting",
    "Beyond Advertising",
];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        project_type: PROJECT_TYPES[0],
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in your name, email and message.");
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(`${API}/contact`, form);
            if (res.data?.success) {
                setSent(true);
                toast.success(
                    "Thanks! Your message reached the AE team — we'll reply within one business day."
                );
                setForm({
                    name: "",
                    email: "",
                    company: "",
                    project_type: PROJECT_TYPES[0],
                    message: "",
                });
            } else {
                toast.error("Something went wrong. Please try again.");
            }
        } catch (err) {
            console.error(err);
            if (err.response?.status === 429) {
                toast.error(
                    "Too many submissions. Please try again in a few minutes."
                );
            } else {
                toast.error(
                    "Couldn't send right now. Email us at info@amplifiedesolutions.com."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-24 md:py-32 bg-ae-gray-100"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
                    {/* Left – Info */}
                    <div className="lg:col-span-5">
                        <div className="inline-flex items-center gap-2 text-ae-orange text-xs font-bold uppercase tracking-[0.22em]">
                            <Mail className="h-4 w-4" /> Contact AE
                        </div>
                        <h2 className="mt-5 font-display font-extrabold text-4xl md:text-5xl text-ae-ink tracking-tight leading-[1.02]">
                            Let&apos;s amplify
                            <br />
                            <span className="text-ae-orange">your next move</span>.
                        </h2>
                        <p className="mt-6 text-base md:text-lg text-ae-gray-700 max-w-md">
                            Whether it&apos;s a campaign, a software build or a digitally
                            enhanced activation — drop us a brief and we&apos;ll come back
                            with ideas.
                        </p>

                        <div className="mt-10 space-y-4">
                            <div
                                className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-black/5 shadow-ae-soft"
                                data-testid="contact-office"
                            >
                                <div className="h-11 w-11 rounded-xl bg-ae-orange/10 text-ae-orange flex items-center justify-center shrink-0">
                                    <MapPin className="h-5 w-5" />
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-widest font-bold text-ae-gray-500">
                                        Office
                                    </div>
                                    <div className="mt-1 font-medium text-ae-ink">
                                        AE is work-from-anywhere. Online all the time.
                                        <br />
                                        Email us if you need our snail mail address.
                                    </div>
                                </div>
                            </div>
                            <a
                                href="mailto:info@amplifiedesolutions.com"
                                className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-black/5 shadow-ae-soft hover:border-ae-orange/40 hover:shadow-ae-lift transition-all group"
                                data-testid="contact-email"
                            >
                                <div className="h-11 w-11 rounded-xl bg-ae-orange/10 text-ae-orange flex items-center justify-center shrink-0 group-hover:bg-ae-orange group-hover:text-white transition-colors">
                                    <Mail className="h-5 w-5" />
                                </div>
                                <div className="flex-1">
                                    <div className="text-xs uppercase tracking-widest font-bold text-ae-gray-500">
                                        Email
                                    </div>
                                    <div className="mt-1 font-medium text-ae-ink">
                                        info@amplifiedesolutions.com
                                    </div>
                                </div>
                                <ArrowUpRight className="h-4 w-4 text-ae-gray-500 group-hover:text-ae-orange transition-colors" />
                            </a>
                            <a
                                href="https://amplifiedesolutions.com"
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-black/5 shadow-ae-soft hover:border-ae-orange/40 hover:shadow-ae-lift transition-all group"
                                data-testid="contact-website"
                            >
                                <div className="h-11 w-11 rounded-xl bg-ae-orange/10 text-ae-orange flex items-center justify-center shrink-0 group-hover:bg-ae-orange group-hover:text-white transition-colors">
                                    <Globe className="h-5 w-5" />
                                </div>
                                <div className="flex-1">
                                    <div className="text-xs uppercase tracking-widest font-bold text-ae-gray-500">
                                        Website
                                    </div>
                                    <div className="mt-1 font-medium text-ae-ink">
                                        amplifiedesolutions.com
                                    </div>
                                </div>
                                <ArrowUpRight className="h-4 w-4 text-ae-gray-500 group-hover:text-ae-orange transition-colors" />
                            </a>
                        </div>
                    </div>

                    {/* Right – Form */}
                    <motion.form
                        onSubmit={submit}
                        data-testid="contact-form"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8 }}
                        className="lg:col-span-7 bg-white rounded-3xl border border-black/5 shadow-ae-soft p-6 md:p-10"
                    >
                        {sent ? (
                            <div
                                data-testid="contact-success"
                                className="min-h-[420px] flex flex-col items-center justify-center text-center"
                            >
                                <div className="h-16 w-16 rounded-full bg-ae-orange/15 text-ae-orange flex items-center justify-center">
                                    <CheckCircle2 className="h-8 w-8" />
                                </div>
                                <h3 className="mt-6 font-display font-extrabold text-3xl text-ae-ink">
                                    Brief received.
                                </h3>
                                <p className="mt-3 text-ae-gray-700 max-w-md">
                                    Thank you. The AE team will reach out within one
                                    business day at the email you provided.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setSent(false)}
                                    data-testid="contact-send-another"
                                    className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-ae-orange hover:underline"
                                >
                                    Send another message
                                    <ArrowUpRight className="h-4 w-4" />
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <Field
                                        label="Your name"
                                        value={form.name}
                                        onChange={handle("name")}
                                        placeholder="Juan Dela Cruz"
                                        testid="contact-name"
                                        required
                                    />
                                    <Field
                                        label="Email"
                                        type="email"
                                        value={form.email}
                                        onChange={handle("email")}
                                        placeholder="you@brand.com"
                                        testid="contact-email-input"
                                        required
                                    />
                                    <Field
                                        label="Company"
                                        value={form.company}
                                        onChange={handle("company")}
                                        placeholder="Your brand or org"
                                        testid="contact-company"
                                    />
                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-[0.18em] text-ae-gray-500 mb-2">
                                            Project type
                                        </label>
                                        <select
                                            value={form.project_type}
                                            onChange={handle("project_type")}
                                            data-testid="contact-project-type"
                                            className="w-full bg-white border border-black/10 rounded-2xl px-4 py-3.5 text-ae-ink focus:outline-none focus:border-ae-orange focus:ring-2 focus:ring-ae-orange/20 transition-all"
                                        >
                                            {PROJECT_TYPES.map((p) => (
                                                <option key={p} value={p}>
                                                    {p}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="mt-5">
                                    <label className="block text-xs font-bold uppercase tracking-[0.18em] text-ae-gray-500 mb-2">
                                        Tell us about your project <span className="text-ae-orange">*</span>
                                    </label>
                                    <textarea
                                        value={form.message}
                                        onChange={handle("message")}
                                        data-testid="contact-message"
                                        required
                                        rows={5}
                                        placeholder="Goals, audience, timing, budget range — anything you can share."
                                        className="w-full bg-white border border-black/10 rounded-2xl px-4 py-3.5 text-ae-ink focus:outline-none focus:border-ae-orange focus:ring-2 focus:ring-ae-orange/20 transition-all resize-none"
                                    />
                                </div>

                                <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <p className="text-xs text-ae-gray-500 max-w-sm">
                                        By submitting, you agree to be contacted by AE
                                        regarding your project. We respect your privacy.
                                    </p>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        data-testid="contact-submit"
                                        className="inline-flex items-center justify-center gap-2 bg-ae-orange text-white rounded-full px-7 py-4 text-sm font-semibold hover:bg-ae-orange-light shadow-ae-lift hover:shadow-ae-glow transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Sending…
                                            </>
                                        ) : (
                                            <>
                                                Send brief
                                                <Send className="h-4 w-4" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </motion.form>
                </div>
            </div>
        </section>
    );
}

function Field({ label, value, onChange, placeholder, type = "text", required, testid }) {
    return (
        <div>
            <label className="block text-xs font-bold uppercase tracking-[0.18em] text-ae-gray-500 mb-2">
                {label} {required && <span className="text-ae-orange">*</span>}
            </label>
            <input
                type={type}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                required={required}
                data-testid={testid}
                className="w-full bg-white border border-black/10 rounded-2xl px-4 py-3.5 text-ae-ink placeholder:text-ae-gray-500 focus:outline-none focus:border-ae-orange focus:ring-2 focus:ring-ae-orange/20 transition-all"
            />
        </div>
    );
}
